// Axios client for the B2C consumer endpoints.
// Mirrors frontend/src/config/axios.config.js but uses a SEPARATE token
// (`consumerToken`) so a consumer session never collides with a staff session.

import axios from 'axios';
import { dedupe } from './dedupe';

const TOKEN_KEY = 'consumerToken';
const GUEST_KEY = 'consumerGuest';   // marks the current token as an ephemeral guest
// The localStorage key other tabs watch: signing in or out in one tab writes it,
// and useConsumerAuth's 'storage' listener follows in every other tab (BUG-054).
export const CONSUMER_TOKEN_KEY = TOKEN_KEY;

// A staff member who opened this borrower's account from View Leads (Open
// borrower account) holds the session in THIS TAB only — sessionStorage, read
// first — so it never replaces a borrower session saved in this browser, and
// ends with the tab (or after its 30 minutes, the server's call).
const STAFF_TOKEN_KEY = 'consumerStaffToken';
const staffToken = () => { try { return sessionStorage.getItem(STAFF_TOKEN_KEY); } catch (_) { return null; } };
export const setStaffToken = (t) => { try { sessionStorage.setItem(STAFF_TOKEN_KEY, t); } catch (_) { /* ignore */ } };
export const isStaffSession = () => !!staffToken();

export const getToken = () => staffToken() || localStorage.getItem(TOKEN_KEY);
// Setting a REAL token clears the guest flag: after a guest registers, their token
// is now a real account's, and must survive a reload (clearGuestSession would
// otherwise wipe it on next load and silently log them out). How long it lasts
// is the server's call (CONSUMER_SESSION_MINUTES), not this file's.
// The flag goes FIRST: other tabs hear the token write as a 'storage' event and
// read the flag then (BUG-054) — the other way round, a real sign-in elsewhere
// would read as a guest one and be ignored.
export const setToken = (t) => { localStorage.removeItem(GUEST_KEY); localStorage.setItem(TOKEN_KEY, t); };
// In a staff tab, ending the session ends only that tab's — never a borrower
// session saved in this browser.
export const clearToken = () => {
  if (staffToken()) { try { sessionStorage.removeItem(STAFF_TOKEN_KEY); } catch (_) { /* ignore */ } return; }
  localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(GUEST_KEY);
};

// Guest sessions (free plan, no signup) are EPHEMERAL — they must not survive a
// page refresh. We flag the token as a guest, and clear it on app load so a
// reload always returns the borrower to a clean landing page (no resumed flow).
// Flag first, as in setToken: another tab must see a guest write AS a guest one.
export const setGuestToken = (t) => { localStorage.setItem(GUEST_KEY, '1'); localStorage.setItem(TOKEN_KEY, t); };
export const isGuestToken = () => localStorage.getItem(GUEST_KEY) === '1';
export const clearGuestSession = () => { if (isGuestToken()) clearToken(); };

// A flow IN PROGRESS. The app clears the token on every full page load (real
// sessions deliberately don't survive a refresh), but a borrower part-way through
// a conversational flow — who refreshes because a page was slow — would then lose
// the flow AND get bounced to /login when its next authenticated call 401s. The
// flow's state lives server-side (the flow lead) and resumes fine, so the ONLY
// thing that must survive that refresh is the token. This flag says "a flow is
// open" — set when a wizard opens, cleared when it finishes or is closed — and the
// load-time logout honours it, keeping the session just long enough to resume.
const FLOW_ACTIVE_KEY = 'consumerFlowActive';
export const markFlowActive = () => { try { localStorage.setItem(FLOW_ACTIVE_KEY, '1'); } catch (_) { /* ignore */ } };
export const clearFlowActive = () => { try { localStorage.removeItem(FLOW_ACTIVE_KEY); } catch (_) { /* ignore */ } };
export const isFlowActive = () => { try { return localStorage.getItem(FLOW_ACTIVE_KEY) === '1'; } catch (_) { return false; } };

// ── Handing off to Google ────────────────────────────────────────────────────
// Leaving for accounts.google.com UNMOUNTS whatever wizard is open, so its
// cleanup runs — and that cleanup clears the flow-active flag above, which is
// the only thing keeping the token alive across the return. The borrower came
// back to a wiped session and a flow restarted at its first question.
//
// This flag says "the unmount you are about to see is a handoff, not a close".
// sessionStorage, not localStorage: it must not outlive the tab, and a stale one
// would wrongly preserve a session on some later unrelated unmount. It is set
// immediately before the redirect and cleared by the callback once the real
// token has been adopted (2026-10-05).
const OAUTH_LEAVING_KEY = 'consumerLeavingForOAuth';
export const markLeavingForOAuth = () => { try { sessionStorage.setItem(OAUTH_LEAVING_KEY, '1'); } catch (_) { /* ignore */ } };
export const clearLeavingForOAuth = () => { try { sessionStorage.removeItem(OAUTH_LEAVING_KEY); } catch (_) { /* ignore */ } };
export const isLeavingForOAuth = () => { try { return sessionStorage.getItem(OAUTH_LEAVING_KEY) === '1'; } catch (_) { return false; } };

// ── Who sent this borrower here ──────────────────────────────────────────────
// A DSA who clicks "Create Customer Portfolio" in the staff app, or who shares
// their link, arrives with `?ref=CODE`. This app is a separate origin with its own
// session and cannot see who they are — the code in the URL is the only carrier.
//
// Captured at MODULE LOAD, deliberately: it has to be read before any effect or
// router redirect rewrites the address bar, and doing it here means no page can
// forget to. Kept in localStorage rather than memory because the referral has to
// survive the reload that a guest session performs.
//
// It is an identifier, not a credential — it attributes a signup and unlocks
// nothing, which is what makes it safe to sit in a forwarded URL.
const REF_KEY = 'consumerRef';

export const getReferral = () => localStorage.getItem(REF_KEY) || undefined;

export const captureReferral = () => {
  try {
    const ref = new URLSearchParams(window.location.search).get('ref');
    // First one wins, matching the server: a borrower who later opens a second
    // agent's link is still the first agent's customer.
    if (ref && /^[A-Za-z0-9]{8}$/.test(ref) && !localStorage.getItem(REF_KEY)) {
      localStorage.setItem(REF_KEY, ref.toUpperCase());
    }
  } catch (e) { /* private mode / no storage — attribution is best-effort */ }
};

captureReferral();

/**
 * Resolve the API base from the BROWSER's hostname — the SAME pattern the staff
 * app uses (frontend/src/utils/api.js). The tenant is the hostname (e.g.
 * `test1.localhost`), shared by the React dev server (:3000) and Laravel (:8000).
 *
 *   Dev:  test1.localhost:3000  (React UI)  →  API at test1.localhost:8000
 *   Prod: tenant.interestng.com (same origin) → API at same host
 *
 * So NO hardcoded proxy/tenant — it follows whatever tenant subdomain you open.
 */
// Same-origin API path. In DEV the CRA proxy (src/setupProxy.js) forwards
// `/api/*` to the tenant backend, so the browser never makes a cross-origin
// request (which the backend's CSP `connect-src` would block). In PROD the app
// is served from the tenant domain, so `/api` is genuinely same-origin.
export const getApiBaseUrl = () => process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
});

// Complete My Profile's saves (ms) — see saveProfileSection.
const PROFILE_SAVE_TIMEOUT = 20000;
// Background-job lookups (ms): an upload carries the borrower's PDFs to the source
// within the one request, so it may run for minutes. A job's status check that finds
// it done also collects and reads the result before it answers (about a minute at
// worst): cut off sooner, that answer — and the fields it filled — would be lost here.
const VERIFICATION_UPLOAD_TIMEOUT = 300000;
const VERIFICATION_CHECK_TIMEOUT = 90000;

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Broadcast when an authenticated call comes back 401, i.e. this session is over:
// the token expired, was revoked from another device, or a password change
// elsewhere invalidated it. ConsumerAuthProvider listens (see useConsumerAuth).
export const UNAUTHORIZED_EVENT = 'consumer:unauthorized';

// Endpoints where a 401 is an ANSWER rather than a dead session. A wrong OTP or a
// failed sign-in must not be treated as "you have been logged out" — that would
// wipe a guest's in-progress flow the moment they fat-finger a code.
const PUBLIC_AUTH_PATHS = [
  '/consumer/login',
  '/consumer/otp/send',
  '/consumer/otp/verify',
  '/consumer/impersonation/exchange',
  '/consumer/register-email',
  '/consumer/login-email',
  '/consumer/guest',
  '/consumer/forgot-password',
  '/consumer/reset-password',
  // `/me` is the session PROBE, not a session USER. ConsumerAuthProvider calls it
  // on every mount to ask "is the token in storage any good?", and a 401 is that
  // question's NO — the honest answer for a token that was revoked, expired, or
  // simply left over from a previous visit. It is not "a live session just died".
  //
  // Treating it as the latter broke the Google return. On that journey the server
  // migrates the guest onto the real account and GuestDataMigrator REVOKES the
  // guest's tokens, so the token sitting in this browser is already dead when the
  // app boots. refresh() probed it, the 401 fired UNAUTHORIZED_EVENT, `expired`
  // went true — and the borrower was shown "Your session ended, so we signed you
  // out for safety" and dumped on /login, all BEFORE GoogleCallback had a chance
  // to adopt the new, valid token that was sitting in the URL.
  //
  // refresh() already handles its own 401 correctly (its catch clears the token
  // and leaves the app cleanly signed out), so nothing is lost by keeping the
  // event out of it — only the false "your session expired" claim goes away.
  '/consumer/me',
  // THE TWO OFFER CALLS THAT RUN DURING A SIGN-IN HAND-OFF.
  //
  // Both of these fire on the Google return, in the window where the OLD guest
  // token has already been revoked server-side (GuestDataMigrator) and the NEW
  // real token is still being adopted by GoogleCallback. A 401 there means "ask
  // again in a moment", not "this borrower's session is over".
  //
  // Treating it as the latter was self-defeating: the interceptor cleared the
  // token and fired UNAUTHORIZED_EVENT, so the very session the caller was
  // waiting for was destroyed by the attempt to use it — and the borrower was
  // shown "Please sign in again to see your offers" while holding a perfectly
  // good token that had just been issued. The callers already handle their own
  // 401 (OffersFlow waits for a real token and retries/falls back), so keeping
  // the event out of these two loses nothing and removes the self-inflicted
  // logout (2026-10-03).
  '/consumer/offers/saved',
  '/consumer/offers/generate',
  // Attaching a mobile to the account the borrower JUST signed into with Google.
  // It is an authenticated route, so a 401 here was read as "your session died",
  // the token was cleared and the borrower was logged out — by the very call that
  // was trying to finish their sign-in. The screen then showed Laravel's raw
  // "Unauthenticated." under six correctly-typed OTP boxes, which tells the
  // borrower nothing and leaves them stuck on the Track-my-loans sign-in step.
  // Its own caller already handles the failure (it shows the real message, or
  // the reuse-vs-refetch choice), so the event is pure harm here (2026-10-03).
  '/consumer/mobile/attach',
];

api.interceptors.response.use(
  (r) => r,
  (error) => {
    const url = error.config?.url || '';
    const isPublicAuth = PUBLIC_AUTH_PATHS.some((p) => url.startsWith(p));
    if (error.response?.status === 401 && !isPublicAuth) {
      clearToken();
      // Clearing the token is NOT enough on its own. `isAuthed` lives in React
      // state and is only recomputed by refresh() on mount, so a mid-session 401
      // used to leave the dashboard mounted and "authenticated" — every panel then
      // fired its own doomed request and the borrower sat on a dead page watching
      // 401s pile up with no way out. This event flips the context, which is what
      // actually lets ProtectedRoute send them to /login.
      window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    }
    return Promise.reject(error);
  }
);

// ── Endpoint wrappers (the whole P1–P8 consumer surface) ────────────────────

// ── DEV: bypass the OTP gate ────────────────────────────────────────────────
// When ON, the chat auto-logs-in a fixed dev consumer (no mobile/OTP typing) so
// you can test the conversational flow directly. Backend auth is untouched — we
// still obtain a real token via the OTP endpoints (dev OTP 123456).
// TURN OFF for real use: set to false (or REACT_APP_DEV_AUTOLOGIN=off).
export const DEV_AUTOLOGIN =
  (process.env.REACT_APP_DEV_AUTOLOGIN ?? 'on') !== 'off';
const DEV_MOBILE = process.env.REACT_APP_DEV_MOBILE || '9999999999';

/** Ensure a consumer token exists; in dev, silently obtain one. Returns true if authed. */
export async function ensureConsumerAuth() {
  if (getToken()) return true;
  if (!DEV_AUTOLOGIN) return false;
  try {
    await consumerApi.otpSend(DEV_MOBILE);
    const { data } = await consumerApi.otpVerify(DEV_MOBILE, '123456');
    if (data?.token) { setToken(data.token); return true; }
  } catch (_) { /* fall through */ }
  return false;
}

/**
 * Ensure SOME consumer session exists before starting a flow — a guest one if the
 * borrower isn't signed in yet. This is what makes registration just-in-time: the
 * whole conversation runs on a throwaway guest token, and the in-flow `register`
 * step later promotes it to a real account (verifyOtp → migrateGuestData moves the
 * flow lead across, so nothing answered is lost). Returns true if a token exists.
 * A real (already signed-in) token is left untouched.
 */
export async function ensureGuestSession() {
  if (getToken()) return true;
  try {
    const { data } = await consumerApi.guest();
    if (data?.token) { setGuestToken(data.token); return true; }
  } catch (_) { /* fall through — caller surfaces a friendly error */ }
  return false;
}

export const consumerApi = {
  // Plans (public)
  plans: () => api.get('/consumer/plans'),
  // The distributor (staff-app) subscription ladder, for the Distributor
  // page's pricing cards. Public — the same plans super admin publishes.
  distributorPlans: () => api.get('/consumer/distributor-plans'),

  // Auth
  // `purpose` ('login' | 'register' | 'bureau') picks the SMS template — they
  // read differently. Omitted → the server infers login/register from whether
  // the mobile exists. 'bureau' must be asked for explicitly: it is the
  // sign-in whose code ALSO carries the credit-pull consent, so the message
  // names the bureau check. Only a flow whose plan declares it should send it.
  otpSend: (mobile, purpose) => api.post('/consumer/otp/send', purpose ? { mobile, purpose } : { mobile }),

  // PAN → name + date of birth, to prefill the About screen.
  //
  // Never throws for a provider problem: the endpoint answers 200 with
  // success:false so the step can fall through to typing. Only a malformed PAN
  // is a 4xx, and the caller checks the format before spending a billed call.
  // The PAN identity lookup. `signal` lets the caller give up on it: the
  // vendor takes 12-17s in practice, and the About screen is not allowed to
  // sit on a spinner for that long with nothing the borrower can do.
  verifyPan: (pan, signal) => api.post('/consumer/pan/verify', { pan }, signal ? { signal } : undefined),
  // `ref` rides on both account-creating calls. The server only reads it when the
  // consumer has no owner yet, so sending it on a plain re-login is harmless.
  // `extra` is the login page's intent: { purpose: 'login' } never creates an
  // account (404 no_account); { purpose: 'register', full_name, email } creates
  // one with those details (409 mobile_registered / email_taken). The in-flow
  // sign-ins send no purpose and keep the old sign-in-or-create behaviour.
  otpVerify: (mobile, otp, extra = {}) => api.post('/consumer/otp/verify', { mobile, otp, ref: getReferral(), ...extra }),
  // Staff opened this account from View Leads: spend the one-time code for the session.
  impersonationExchange: (code) => api.post('/consumer/impersonation/exchange', { code }),
  // Bind a VERIFIED mobile to the account already signed in (the Google path).
  // NOT otpVerify: that upserts by mobile and would create a second account,
  // orphaning the flow this borrower is in. No token comes back — the caller
  // is already authenticated and keeps the one it has.
  // `extra.full_name` saves the name the borrower confirmed on the same screen.
  // A 409 mobile_owned_elsewhere carries `switch_ticket`: mobileSwitch(ticket)
  // signs them into the account that owns the number (the code proved it).
  mobileAttach: (mobile, otp, extra = {}) => api.post('/consumer/mobile/attach', { mobile, otp, ...extra }),
  mobileSwitch: (ticket) => api.post('/consumer/mobile/switch', { ticket }),
  // No checkMobile / checkEmail any more (BUG-024): asking "does this number or
  // email have an account?" was a lookup anyone could run. The screens try the
  // password or send the code, and the server answers the same either way.
  login: (mobile, password) => api.post('/consumer/login', { mobile, password }),
  // Email register/login — the alternative to mobile OTP (password-based, no OTP).
  // `registerEmail` creates an email account; `loginEmail` signs one in. Both
  // account-creating calls carry `ref` for referral attribution, like the mobile path.
  registerEmail: (email, password, password_confirmation, full_name) =>
    api.post('/consumer/register-email', { email, password, password_confirmation, full_name, ref: getReferral() }),
  loginEmail: (email, password) => api.post('/consumer/login-email', { email, password }),
  guest: () => api.post('/consumer/guest', { ref: getReferral() }),   // free plan: no mobile, no OTP
  // `password_ticket` is the one otp/verify handed back with the session: the
  // server writes a password only with that proof of a just-verified code
  // (BUG-048) — 403 reauth_required without it, or once it is spent / 10 min old.
  setPassword: (password, password_confirmation, password_ticket) =>
    api.post('/consumer/set-password', { password, password_confirmation, password_ticket }),
  // Changing an EXISTING password — verifies the current one and signs other
  // devices out. Not the same call as setPassword (the first-time step).
  changePassword: (current_password, password, password_confirmation) =>
    api.post('/consumer/change-password', { current_password, password, password_confirmation }),
  // Forgot password, in three steps. (1) forgotPassword sends an OTP to the
  // number on the account — answering the same whether or not there is one
  // (BUG-024). (2) the EXISTING otpVerify (purpose 'login') checks that code and
  // returns a token and a password_ticket — so a wrong code is caught before the
  // borrower composes a password, not after. (3) resetPassword rewrites the
  // password with that ticket, and signs every other device out.
  forgotPassword: (mobile) => api.post('/consumer/forgot-password', { mobile }),
  resetPassword: (password, password_confirmation, password_ticket) =>
    api.post('/consumer/reset-password', { password, password_confirmation, password_ticket }),
  me: () => api.get('/consumer/me'),
  logout: () => api.post('/consumer/logout'),

  // Dashboard
  dashboard: () => api.get('/consumer/dashboard'),
  // Account › My requests: the support requests the borrower's runs opened and
  // the ones they raised, each with the team's replies.
  myRequests: (params) => api.get('/consumer/support/requests', { params }),
  supportContact: () => api.get('/consumer/support/contact'),
  // Help & Support / My Requests › Raise a request: { message } → { ref, status } (BUG-052).
  raiseSupportRequest: (body) => api.post('/consumer/support/requests', body),
  // Profile: account + the KYC collected across the borrower's flows.
  profile: () => api.get('/consumer/profile'),
  // Edits the ACCOUNT-owned fields only ({ full_name, email }). Responds with the
  // full profile payload, so callers can replace their state rather than merge.
  updateProfile: (payload) => api.put('/consumer/profile', payload),

  // Existing Loan Details (borrower's own version of the Lead Wizard step).
  // Reuses the SAME calc engine + storage as staff, scoped to the consumer's lead.
  loansGet: (leadId) => api.get('/consumer/loans', { params: leadId ? { lead_id: leadId } : {} }),
  // New Loans › Eligibility Headroom: what they could still borrow per product,
  // from the lenders' own eligibility formulas (ConsumerEligibilityHeadroom).
  eligibilityHeadroom: (leadId) => api.get('/consumer/eligibility/headroom', { params: leadId ? { lead_id: leadId } : {} }),
  loanTypes: () => api.get('/consumer/loans/types'),
  loanCalculate: (loan) => api.post('/consumer/loans/calculate', loan),
  loansSave: (loans, leadId) => api.post('/consumer/loans', leadId ? { loans, lead_id: leadId } : { loans }),
  loanDelete: (loanId) => api.delete(`/consumer/loans/${loanId}`),

  // Loan Optimisation — the plan, worked out on the server for this borrower's
  // own report with the super admin's published rates. status: ok | no_report | not_configured.
  loanOptimisation: (leadId, params = {}) => api.get('/consumer/loan-optimisation', { params: { ...(leadId ? { lead_id: leadId } : {}), ...params } }),
  // { objective?, prepay_budget?, rate_overrides?: { loanId: % a year } }
  loanOptimisationWhatIf: (leadId, body) => api.post('/consumer/loan-optimisation/what-if', { ...(leadId ? { lead_id: leadId } : {}), ...body }),
  // Asks the lenders' own offer engine to price each transfer (slow: one engine run per loan).
  loanOptimisationLenderOffers: (leadId) => api.post('/consumer/loan-optimisation/lender-offers', leadId ? { lead_id: leadId } : {}, { timeout: 180000 }),
  loanDocUpload: (loanId, docType, file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('doc_type', docType);
    return api.post(`/consumer/loans/${loanId}/documents`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000,
    });
  },
  loanDocDelete: (loanId, docType) => api.delete(`/consumer/loans/${loanId}/documents/${docType}`),
  // Record the borrower's decision after comparing the loan with a document:
  // { confirmed: [field], replaced: [field], kept: [field] }.
  loanDocReconcile: (loanId, docType, decision) =>
    api.post(`/consumer/loans/${loanId}/documents/${docType}/reconcile`, decision),
  // Balance-Transfer offers — live offer engine → published market-rate
  // fallback. Returns { best, all, source, refine, marketMeta? }. Saved loans
  // call the id path; unsaved bureau rows (Portfolio tab) post the loan payload
  // inline. `refine` (optional) carries employer / salary-bank overrides the
  // borrower supplies in the dialog form to unlock more lenders.
  loanBt: (loanId, refine = {}) => api.post(`/consumer/loans/${loanId}/bt`, refine, { timeout: 60000 }),
  loanBtInline: (loan, leadId, refine = {}) =>
    api.post('/consumer/loans/bt', { loan, ...(leadId ? { lead_id: leadId } : {}), ...refine }, { timeout: 60000 }),
  // Fetch the stored file as a blob (auth token added by the interceptor) so the
  // borrower can view their uploaded document. The view endpoint is auth-gated.
  loanDocView: (loanId, docType) =>
    api.get(`/consumer/loans/${loanId}/documents/${docType}/view`, { responseType: 'blob' }),

  // Closed Loans → "Enter Loan Details": the borrower's own figures for a closed
  // loan on their report. Kept apart from the active loans (never loansSave).
  // `key` = the account's account_key from the dashboard; `leadId` = the run.
  closedLoanSave: (key, leadId, loan) => api.put(`/consumer/closed-loans/${key}`, { lead_id: leadId, loan }),
  closedLoanReset: (key, leadId) => api.delete(`/consumer/closed-loans/${key}`, { params: { lead_id: leadId } }),
  // Documents on a SAVED closed loan (its row id) — the same four calls as above.
  closedLoanDocUpload: (id, docType, file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('doc_type', docType);
    return api.post(`/consumer/closed-loans/docs/${id}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000,
    });
  },
  closedLoanDocDelete: (id, docType) => api.delete(`/consumer/closed-loans/docs/${id}/${docType}`),
  closedLoanDocReconcile: (id, docType, decision) =>
    api.post(`/consumer/closed-loans/docs/${id}/${docType}/reconcile`, decision),
  closedLoanDocView: (id, docType) =>
    api.get(`/consumer/closed-loans/docs/${id}/${docType}/view`, { responseType: 'blob' }),

  // Product-driven "Generate offers" flow — live option sources for `select` steps.
  // { mine: 1 } = only the loans for how the borrower earns (their profile)
  products: (params) => api.get('/consumer/products', params ? { params } : undefined),
  // Borrower page "Compare offers": chip rows + rate cards from the tenant's
  // own products and pricing tiers. No args = the unfiltered lists.
  offerExplorer: (params = {}) => api.get('/consumer/offer-explorer', { params }),
  // { lenders_total, lenders_by_type, as_of } | null — the one lender count the
  // public pages' copy uses (BUG-072; shared/useLenderCount).
  lenderStats: () => api.get('/consumer/lender-stats'),
  // The tenant's approved lending network, plus the bank / product /
  // expert-type facets computed over the WHOLE network (a filtered call still
  // carries the full rows). Its only consumer, the Borrower page's "Talk to an
  // RM" panel (useExperts), was removed on request 2026-09-04; the endpoint
  // and this client stay for the next caller.
  experts: (params = {}) => api.get('/consumer/experts', { params }),
  // Read-only: report plans the borrower bought + which are still valid.
  subscriptions: () => api.get('/consumer/subscriptions'),
  // Read-only: every uploaded loan document, gathered across all loans.
  documents: () => api.get('/consumer/documents'),
  // "Vet a Letter" section: history of vetted sanction letters (terms + PDF + date).
  vettedLetters: () => api.get('/consumer/vetted-letters'),
  // "Vet a Letter" detail: analysis (flags/prepayment/market) + amortization schedule.
  vettedLetterAnalysis: (id) => api.get(`/consumer/vetted-letters/${id}/analysis`),
  // "Rejection Roadmaps" section: captured rejection cases + rebuilt 5-step roadmaps.
  rejectionRoadmaps: () => api.get('/consumer/rejection-roadmaps'),
  // The lender check for one filed case — run once, then stored on the case.
  rejectionAnalysis: (caseId) => api.post(`/consumer/rejection-roadmaps/${caseId}/analysis`),
  // "Complete your profile" — the builder-defined profile form + the borrower's
  // saved answers. One template (template_type='profile') serves every loan type;
  // each field's mapping feeds offer generation. See ConsumerProfileController.
  profileTemplate: () => api.get('/consumer/profile/template'),
  profileComplete: () => api.get('/consumer/profile/complete'),
  // The profile's saves give up after a while: a request hung on a dead connection
  // would otherwise hold the step (and the rail waiting on it) indefinitely — a save
  // that times out is kept as "not saved" and sent again.
  saveProfileSection: (answers, verify = null) =>
    api.put('/consumer/profile/complete', { answers, ...(verify ? { verify } : {}) }, { timeout: PROFILE_SAVE_TIMEOUT }),
  // Pull the latest bureau report + flow answers into any empty profile fields.
  // Response.data.filled === 0 means there was nothing to prefill from.
  prefillProfile: () => api.post('/consumer/profile/prefill'),
  // Applicants — the borrower (ref "primary") and co-applicants (ref "co:{id}")
  // through ONE owner-scoped surface. Every per-applicant read/write names the
  // ref, so a co-applicant's answers and lookups never touch the borrower's.
  applicants: () => api.get('/consumer/applicants'),
  addApplicant: (relationship, employmentStatusId, declaration = true) =>
    api.post('/consumer/applicants', {
      relationship,
      employment_status_id: employmentStatusId || null,
      declaration,
    }),
  removeApplicant: (ref) => api.delete(`/consumer/applicants/${ref}`),
  applicantForm: (ref) => api.get(`/consumer/applicants/${ref}/form`),
  saveApplicantAnswers: (ref, answers) => api.put(`/consumer/applicants/${ref}/answers`, { answers }, { timeout: PROFILE_SAVE_TIMEOUT }),
  // Several ways of earning: tick / untick / move the ★ (409 asks to confirm a
  // removal that deletes answers), and one OTHER way's own answers.
  setWays: (ref, statusIds, main, confirmRemove = false) =>
    api.put(`/consumer/applicants/${ref}/ways`, { status_ids: statusIds, main, confirm_remove: confirmRemove }, { timeout: PROFILE_SAVE_TIMEOUT * 2 }),
  saveWayAnswers: (ref, statusId, answers) =>
    api.put(`/consumer/applicants/${ref}/ways/${statusId}/answers`, { answers }, { timeout: PROFILE_SAVE_TIMEOUT }),
  // Verification APIs attached to an applicant's sections (Form Builder →
  // section → APIs): the rows (inputs, consent, state), Fetch, View, and the
  // co-applicant's own OTP consent.
  applicantVerifications: (ref) => api.get(`/consumer/applicants/${ref}/verifications`),
  // `way`: the row's other way of earning (row.way) — the lookup then fills that way's step.
  runVerification: (ref, serviceId, inputs = {}, way = null) =>
    api.post(`/consumer/applicants/${ref}/verifications/run`, { service_id: serviceId, inputs, ...(way ? { way } : {}) }),
  verificationReport: (ref, id) => api.get(`/consumer/applicants/${ref}/verifications/${id}`),
  // OTP lookups (the "With OTP" tab): Send OTP — also Resend, the same call —
  // then the OTP with the SAME inputs (the server checks they match step 1).
  // Also starts a background job that takes no files; `credentials` = { [secret input]:
  // string } (a portal sign-in's password) goes once as `credentials` — never under
  // `inputs`, never kept here.
  startVerification: (ref, serviceId, inputs = {}, way = null, { credentials } = {}) => {
    const sent = Object.fromEntries(Object.entries(credentials || {})
      .filter(([, v]) => typeof v === 'string' && v.trim() !== ''));
    return api.post(`/consumer/applicants/${ref}/verifications/start`, {
      service_id: serviceId, inputs, ...(way ? { way } : {}), ...(Object.keys(sent).length ? { credentials: sent } : {}),
    });
  },
  submitVerificationOtp: (ref, id, otp, inputs = {}) =>
    api.post(`/consumer/applicants/${ref}/verifications/${id}/otp`, { otp, inputs }),
  // Source-page lookups (DigiLocker, GST): has the borrower finished on the source's page?
  // No time limit here, as before — a finished check also fetches the record (GLIB: up to
  // two calls of two minutes each), and an answer cut off here would be lost.
  // Background jobs (documents the source analyses), `{ job: true }`: is the analysis done
  // yet? Up to VERIFICATION_CHECK_TIMEOUT, above the longest such check.
  checkVerification: (ref, id, { job = false } = {}) =>
    api.post(`/consumer/applicants/${ref}/verifications/${id}/check`, undefined,
      job ? { timeout: VERIFICATION_CHECK_TIMEOUT } : undefined),
  // Background-job lookups (bank statements, ITR / GST PDFs): the lookup's typed inputs
  // and its documents in ONE multipart request. `files` = { [file input]: File[] }, each
  // sent under a neutral name (`statements-1.pdf` …) — the borrower's own file names never
  // leave the browser. `credentials` = { [secret input]: string } (a locked PDF's
  // password): sent once, as `credentials[name]` — never under `inputs`, never kept here.
  // The explicit multipart header matters: with the JSON default, axios would send the
  // FormData as JSON.
  uploadVerification: (ref, serviceId, inputs = {}, files = {}, way = null, { onUploadProgress, signal, credentials } = {}) => {
    const fd = new FormData();
    fd.append('service_id', String(serviceId));
    if (way) fd.append('way', way);
    Object.entries(inputs || {}).forEach(([k, v]) => {
      if (v === true) fd.append(`inputs[${k}]`, '1');
      else if (v !== false && v != null && v !== '') fd.append(`inputs[${k}]`, String(v));
    });
    Object.entries(files || {}).forEach(([name, list]) => {
      (list || []).forEach((f, i) => fd.append(`files[${name}][]`, f, `${name}-${i + 1}.pdf`));
    });
    Object.entries(credentials || {}).forEach(([k, v]) => {
      if (typeof v === 'string' && v.trim() !== '') fd.append(`credentials[${k}]`, v);
    });
    return api.post(`/consumer/applicants/${ref}/verifications/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: VERIFICATION_UPLOAD_TIMEOUT,
      ...(onUploadProgress ? { onUploadProgress } : {}),
      ...(signal ? { signal } : {}),
    });
  },
  // Stop a running background job (the borrower's "Stop"): no call to the source, nothing filled.
  cancelVerification: (ref, id) => api.post(`/consumer/applicants/${ref}/verifications/${id}/cancel`),
  // A co-applicant's OTP consent. With `serviceId` (a background-job lookup) the OTP
  // approves THAT lookup only, in its own words; without it, as before.
  applicantConsent: (ref, action, code = null, serviceId = null) =>
    api.post(`/consumer/applicants/${ref}/consent`, {
      action,
      ...(code ? { code } : {}),
      ...(serviceId ? { service_id: serviceId } : {}),
    }),

  // Offers Wizard (Product → questions → offers). `offerQuestions` returns the
  // product-specific questions NOT already answered by the saved profile (gaps),
  // plus the prefilled ones for display. `generateOffers` builds the engine input
  // from the profile + these answers and runs the same engine as the staff wizard.
  offerQuestions: (productId) => api.get(`/consumer/offers/questions/${productId}`),
  // `opts` = { signal, timeout } lets a caller give up on the run (Compare offers
  // does, BUG-036); a caller that passes none waits as before.
  generateOffers: (productId, answers = {}, companyId = null, { signal, timeout } = {}) =>
    api.post('/consumer/offers/generate', {
      productId,
      answers,
      ...(companyId ? { companyId } : {}),
    }, { ...(signal ? { signal } : {}), ...(timeout ? { timeout } : {}) }),
  // The borrower's most recently generated offers (from the chat flow or the
  // wizard), stored on their lead. Drives the "saved offers first" landing of
  // Dashboard → View Offers; { data: { has_saved, offers, best, product, ... } }.
  savedOffers: () => api.get('/consumer/offers/saved'),
  // View Offers landing: every loan product on the plan with its state
  // (locked / indicative / accurate), what it still needs, and its newest saved
  // offers. { data: { products: [{ product, state, needs:{min,full}, offers }] } }
  offersOverview: () => api.get('/consumer/offers/overview'),
  // "Interested" and "Pin bank" on a set of saved offers (BUG-003), kept on the
  // lead those offers belong to (the borrower's own) for the tenant's RM. Each
  // answers the marks as they now stand: { data: { interest, pinned_banks } }.
  // `banks` is the WHOLE pinned list (at most 2), so the latest call wins.
  offerMarks: (leadId) => api.get('/consumer/offers/marks', { params: { lead_id: leadId } }),
  markOfferInterest: (leadId, offerId) => api.post('/consumer/offers/interest', { lead_id: leadId, offer_id: offerId }),
  withdrawOfferInterest: (leadId) => api.delete('/consumer/offers/interest', { params: { lead_id: leadId } }),
  setPinnedBanks: (leadId, banks) => api.put('/consumer/offers/pins', { lead_id: leadId, banks }),
  // Analysis & Recommendation — the AI narrative for the frontend-computed facts.
  // We send the facts (all numbers) and get back only the prose ({narrative,
  // generated_at, cached} or narrative:null → the client uses its template copy).
  // `refresh:true` bypasses the server-side cache and re-writes the narrative.
  // `leadId` is the report being viewed — the narrative is cached on it, so
  // switching between reports does not rewrite each other's text.
  analysisNarrative: (facts, refresh = false, leadId = null) =>
    api.post('/consumer/analysis/narrative', {
      facts,
      ...(refresh ? { refresh: true } : {}),
      ...(leadId ? { lead_id: leadId } : {}),
    }),

  productSalaryBanks: (productId) => api.get(`/consumer/products/${productId}/salary-banks`),
  // `search` = type-to-find. `opts.ids` = resolve a KNOWN selection (comma-separated
  // company ids) back to labelled options, so a saved employer can be pre-selected
  // as a chip — the flow stores ids but never the employer name.
  productCompanies: (productId, search, opts = {}) =>
    api.get(`/consumer/products/${productId}/companies`, {
      params: opts.ids ? { ids: opts.ids } : { search },
    }),

  // Employers by NAME, with no product in the picture — what the Track-my-loans
  // flow asks, since it never picks a product to scope a company list by.
  // Returns plain strings, not {id,title}: this question stores the name.
  employers: (search, limit = 20) =>
    api.get('/consumer/employers', { params: { search, limit } }),

  // Conversational flow
  // `force` = fetch a FRESH paid report even though a valid one exists (the user
  // chose "get a new report" over viewing the old).
  // `seed` (optional) pre-answers matching questions from data a page already
  // collected (e.g. the Rejected tab's loan type/amount/reason). The backend
  // allow-lists the keys, so anything extra is ignored.
  flowStart: (plan_revision_id, force = false, seed = null) => {
    const run = () => api.post('/consumer/flow/start', {
      plan_revision_id,
      ...(force ? { force: true } : {}),
      ...(seed && Object.keys(seed).length ? { seed } : {}),
    });
    // A plain start (no force, no seed) is the one React 18 StrictMode fires
    // TWICE on mount — two full server round-trips that each rewrite the flow
    // lead and, on a single-worker dev server, serialize and DOUBLE the
    // "Loading your options…" wait. Share ONE in-flight request for it (ttl 0 =
    // collapse concurrent duplicates only, never serve a stale cached start, so
    // a later legitimate start still runs). A forced refetch or a seeded start
    // is intentional and is NEVER deduped.
    if (!force && !(seed && Object.keys(seed).length)) {
      return dedupe(`flow-start:${plan_revision_id}`, run, 0);
    }
    return run();
  },
  // Launch a HARDCODED flow (sanction letter / rejected roadmap) — no plan row.
  // `flow` = 'sanction' | 'rejected'.
  flowStartHardcoded: (flow, seed = null) =>
    api.post('/consumer/flow/start-hardcoded', {
      flow,
      ...(seed && Object.keys(seed).length ? { seed } : {}),
    }),
  // Resume an in-progress flow — the recovery path for a paid user who got stuck.
  flowCurrent: () => api.get('/consumer/flow/current'),
  // "How do you earn?" choices for the borrower's flow (Employment Statuses master)
  flowEarningOptions: () => api.get('/consumer/flow/earning-options'),
  // `extra.state`: the state the borrower chose for a pincode our list does not have.
  flowAnswer: (key, value, extra = {}) => api.post('/consumer/flow/answer', { key, value, ...extra }),
  // Correct an answer already given, from the live lead card. Does NOT advance the
  // flow — see ConsumerFlowController::edit.
  flowEdit: (key, value, extra = {}) => api.post('/consumer/flow/edit', { key, value, ...extra }),
  // The "We used your saved details" card: no change = Looks right; change = keys to ask again.
  flowKnown: (change = []) => api.post('/consumer/flow/known', change.length ? { change } : {}),
  // What a signed-in borrower already told us — Compare offers' saved-details card.
  flowKnownAnswers: () => api.get('/consumer/flow/known-answers'),
  flowTeaserContinue: () => api.post('/consumer/flow/teaser/continue'),
  // `opts` = { signal, timeout }: Compare offers stops waiting on a slow read and
  // lets the borrower type instead (BUG-050). Without them it waits as before.
  flowOcr: (file, { signal, timeout } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/consumer/flow/ocr', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      ...(signal ? { signal } : {}),
      ...(timeout ? { timeout } : {}),
    });
  },
  flowOcrSkip: () => api.post('/consumer/flow/ocr/skip'),
  // Regenerate Credit Report (dashboard): the last report's details + price,
  // then start a forced re-run (consent → pay → fetch) with confirmed details.
  regenerateShow: () => api.get('/consumer/flow/regenerate'),
  regenerateStart: (details) => api.post('/consumer/flow/regenerate', details),
  // `confirmActivePlan`: the payment id of the 12-month plan the borrower
  // already holds, sent once they have confirmed a second paid pull (BUG-043);
  // without it the server answers 409 active_plan_confirm_required.
  flowPayInitiate: (confirmActivePlan = null) => api.post('/consumer/flow/pay/initiate',
    confirmActivePlan ? { confirm_active_plan: confirmActivePlan } : {}),
  flowPayConfirm: (payload) => api.post('/consumer/flow/pay/confirm', payload),
  flowConsentSend: () => api.post('/consumer/flow/consent/send'),
  // consentVersion: the words shown, when consent is asked at the pull (F9).
  flowConsentVerify: (otp, consentVersion) => api.post('/consumer/flow/consent/verify', { otp, consent_version: consentVersion }),
  // The run is waiting to pull a report but the account has no mobile (a Google
  // sign-in): the code sent with otpSend(mobile, 'bureau') verifies the number
  // AND stands as the consent, then the pull runs. Same reply as flowConsentVerify.
  flowConsentAttachMobile: (mobile, otp, consentVersion) =>
    api.post('/consumer/flow/consent/attach-mobile', { mobile, otp, consent_version: consentVersion }),
  // Advance past the in-flow `register` step, AFTER otp/verify promoted the guest
  // to a real account (which re-parented the flow lead). Returns the next step.
  flowRegisterResolve: () => api.post('/consumer/flow/register/resolve'),
  flowRetry: () => api.post('/consumer/flow/retry'),
  // The plan's other bureau after its own gave no report — free once per payment.
  // Body: fullname, pan, gender, dob, pincode, state?, consent, consent_version.
  flowFallback: (details) => api.post('/consumer/flow/fallback', details),
  // Ask the bureau again with the mobile the borrower's loans use (B6): a code
  // to that number, then verify it — the retry runs with it.
  flowMobileSend: (mobile) => api.post('/consumer/flow/mobile/send', { mobile }),
  flowMobileVerify: (otp, consentVersion) => api.post('/consumer/flow/mobile/verify', { otp, consent_version: consentVersion }),
  flowSanction: ({ skip, file }) => {
    if (skip) return api.post('/consumer/flow/sanction', { skip: true });
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/consumer/flow/sanction', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // "Vet a sanction letter" plan: upload the letter (runs OCR → returns the review
  // step with parsed terms), then confirm the reviewed/edited terms (advances the
  // flow into register → save-as-loan).
  flowSanctionUpload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/consumer/flow/sanction/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
  },
  flowSanctionConfirm: (fields) => api.post('/consumer/flow/sanction/confirm', fields),
  // Rejection roadmap: confirm the review screen (BUG-042). `acknowledged` names
  // each flagged reason the borrower says the lender did give.
  flowReviewConfirm: (acknowledged = []) => api.post('/consumer/flow/review', { acknowledged }),
  portfolioShare: (channel) => api.post('/consumer/flow/portfolio/share', { channel }),
  // Password-protected PDF of a bureau report the borrower already fetched.
  // Rendered from the JSON stored at fetch time, so this is free — it never
  // triggers a second (paid) bureau call. Comes back as a blob; the PDF is
  // locked with the last 5 characters of the PAN + "PAN".
  // Longer timeout than a normal call: rendering a full CIR is CPU work.
  bureauReportPdf: (reportId) =>
    api.post(`/consumer/bureau-report/${reportId}/download`, {}, { responseType: 'blob', timeout: 60000 }),
  // The topbar eye's security PIN (the staff app's masking PIN, for borrowers).
  maskingPinStatus: () => api.get('/consumer/masking-pin/status'),
  setMaskingPin: (pin, pinConfirmation, currentPin = null) =>
    api.post('/consumer/masking-pin', { pin, pin_confirmation: pinConfirmation, ...(currentPin ? { current_pin: currentPin } : {}) }),
  verifyMaskingPin: (pin) => api.post('/consumer/masking-pin/verify', { pin }),
  resetMaskingPin: (password, newPin, newPinConfirmation) =>
    api.post('/consumer/masking-pin/reset', { password, new_pin: newPin, new_pin_confirmation: newPinConfirmation }),
  // The same report to read on screen: the PDF's HTML, no file and no password.
  bureauReportView: (reportId) => api.post(`/consumer/bureau-report/${reportId}/view`),

  // Public Lender page: the registration form (name, mobile, institution and a
  // few questions) — LenderRegisterModal.js and the onboarding wizard. No token
  // needed: the reader is not a user yet. Same-origin like everything above.
  lenderRegister: (payload) => api.post('/lender/register', payload),
};

export default api;
